/**
 * A button that opens a small schema-validated form in a modal (e.g. "Change rent",
 * "End tenancy", "Record cost"). Refreshes all data after a successful submit.
 */
import type { Schema } from "@shared/validation.ts";
import { type ReactNode, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormProvider } from "react-hook-form";
import { type Check, useSchemaForm } from "../../lib/forms.ts";
import { useInvalidateAll } from "../../lib/query.ts";
import { SubmitButton } from "../ui/kit.tsx";
import { type AnyForm, FormError } from "./fields.tsx";

interface ModalFormProps<T> {
  title: string;
  schema: Schema<T>;
  checks?: Check<T>[];
  defaults: Record<string, unknown>;
  onSubmit: (value: T) => Promise<unknown>;
  submitLabel?: string;
  children: (form: AnyForm) => ReactNode;
  size?: "sm" | "lg" | "xl";
}

function ModalFormBody<T>({ onHide, ...props }: ModalFormProps<T> & { onHide: () => void }) {
  const invalidate = useInvalidateAll();
  const { form, formError, submit } = useSchemaForm(props.schema, props.defaults, props.checks);
  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={submit(async (value) => {
          await props.onSubmit(value);
          await invalidate();
          onHide();
        })}
      >
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="h5">
            {props.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormError message={formError} />
          {props.children(form)}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide}>
            Cancel
          </Button>
          <SubmitButton busy={form.formState.isSubmitting}>{props.submitLabel ?? "Save"}</SubmitButton>
        </Modal.Footer>
      </form>
    </FormProvider>
  );
}

export function ModalFormButton<T>({
  label,
  variant = "outline-primary",
  buttonSize = "sm",
  ...props
}: ModalFormProps<T> & { label: ReactNode; variant?: string; buttonSize?: "sm" | "lg" | "md" }) {
  const [show, setShow] = useState(false);
  return (
    <>
      <Button
        variant={variant}
        size={buttonSize === "md" ? undefined : buttonSize}
        onClick={() => setShow(true)}
      >
        {label}
      </Button>
      <Modal show={show} onHide={() => setShow(false)} size={props.size} centered>
        {show && <ModalFormBody {...props} onHide={() => setShow(false)} />}
      </Modal>
    </>
  );
}
